export enum AutomationTypeEnum {
  /** Customer completed booking - send confirmation/thank-you */
  BookingConfirmation = 'booking-confirmation',
  /** Customer canceled an appointment */
  BookingCanceled = 'booking-canceled',
  /** Business owner rescheduled an appointment (changed its date/time) */
  AppointmentRescheduled = 'appointment-rescheduled',
  /** Customer started scheduling but abandoned mid-flow */
  AbandonedScheduling = 'abandoned-scheduling',
  /** Customer hasn't visited in a long time */
  InactiveCustomer = 'inactive-customer',
  /** X days passed since last appointment */
  PostVisitFollowUp = 'post-visit-follow-up',
  /** Customer birthday greeting */
  BirthdayGreeting = 'birthday-greeting',
  /** Smart Nudge - proactive re-engagement based on visit cycle */
  SmartNudge = 'smart-nudge',
  /** Customer was a no-show */
  NoShowFollowUp = 'no-show-follow-up',
  /** Ask for review after appointment */
  ReviewRequest = 'review-request',
  /** Welcome message for first-time customers */
  NewCustomerWelcome = 'new-customer-welcome',
  /** Same-day reminder sent morning of appointment */
  SameDayReminder = 'same-day-reminder',
  /** Appointment reminder X days before */
  AppointmentReminder = 'appointment-reminder',
  /** Waiting list slot notification */
  WaitingListNotification = 'waiting-list-notification',
  /** Send invoice/receipt link after a successful payment (PayMe invoices only) */
  InvoiceReceipt = 'invoice-receipt',
  /** Ask a tracked customer to send a follow-up face photo every X days (skin feature) */
  SkinScanReminder = 'skin-scan-reminder',
  /** A direct product order is ready for pickup — notify the customer */
  ProductOrderReady = 'product-order-ready',
}

/**
 * Cap on the business-written paragraph inside a WhatsApp automation template. Keeps the
 * rendered body well under Meta's 1024-character template limit.
 */
export const WHATSAPP_FREE_TEXT_MAX_LENGTH = 300;

/**
 * Default business-written paragraph for automations whose WhatsApp template has one
 * (`settings.whatsappFreeText`). Used until the owner writes their own.
 */
export const WHATSAPP_FREE_TEXT_DEFAULTS: Partial<Record<AutomationTypeEnum, string>> = {
  [AutomationTypeEnum.NewCustomerWelcome]: 'אנחנו כאן לכל שאלה לפני הביקור הראשון שלך.',
  [AutomationTypeEnum.InactiveCustomer]: 'מתגעגעים אלייך ונשמח לפנק אותך בביקור הבא.',
  [AutomationTypeEnum.PostVisitFollowUp]: 'מקווים שנהנית מהביקור, נשמח לשמוע איך היה.',
  [AutomationTypeEnum.BirthdayGreeting]: 'מחכה לך פינוק מאיתנו בביקור הבא.',
};

/** Who the post-visit follow-up goes to (`settings.audience` of PostVisitFollowUp). */
export enum PostVisitAudienceEnum {
  /** Every customer after every appointment (the original behavior; absent => this). */
  All = 'all',
  /** Only customers whose appointment was their first at the business. */
  NewCustomers = 'new-customers',
}

export enum AutomationChannelEnum {
  SMS = 'sms',
  WhatsApp = 'whatsapp',
  Both = 'both',
}

export interface IAutomationConfig {
  id?: string;
  bid?: string;
  type: AutomationTypeEnum;
  /** Whether this automation is active */
  enabled: boolean;
  /** Channel to send messages through */
  channel: AutomationChannelEnum;
  /** Custom message template (Hebrew). Supports parameter tags like {שם-הלקוח} */
  message: string;
  /** Type-specific settings */
  settings: Record<string, any>;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/** Metadata describing an automation type for UI rendering */
export interface IAutomationTypeMeta {
  type: AutomationTypeEnum;
  name: string;
  description: string;
  icon: string;
  color: string;
  /** Which settings fields this type supports */
  settingsSchema: IAutomationSettingField[];
  /** Default SMS message template */
  defaultMessage: string;
  /** Default WhatsApp message template (falls back to defaultMessage if not set) */
  defaultWhatsAppMessage?: string;
  /**
   * Set for types whose WhatsApp template carries a business-written paragraph. The text is
   * stored in `settings.whatsappFreeText`; `defaultWhatsAppMessage` marks its slot with
   * {טקסט-חופשי}.
   */
  whatsAppFreeText?: { defaultText: string };
  /** Whether WhatsApp is supported for this type */
  supportsWhatsApp: boolean;
  /** If true, WhatsApp is only available when the business has its own WhatsApp Business account connected (no Timeit-number fallback). */
  requiresBusinessWhatsApp?: boolean;
  /** If true, automation is only available when the business has a supported invoice provider configured (e.g. PayMe or SmartBee). */
  requiresInvoiceProvider?: boolean;
  /** If true, automation is only shown to businesses with the Skin Analysis feature enabled. */
  requiresSkinFeature?: boolean;
  /** If true, automation is only shown to businesses with the Shop feature enabled. */
  requiresShop?: boolean;
  /** Available SMS parameter tags for this type */
  availableTags: string[];
  /** If true, every tag in availableTags must appear in the SMS message — used for messages that can't function without all placeholders. */
  allTagsMandatory?: boolean;
  /** Subset of availableTags that must appear in the SMS message. Takes precedence over allTagsMandatory when set. */
  mandatoryTags?: string[];
  /** If true, automation is always on and cannot be toggled off */
  alwaysOn?: boolean;
  /** If true, automation is enabled by default (can still be toggled off). Used so existing
   * behavior is preserved for businesses that never explicitly configured this automation. */
  defaultOn?: boolean;
  /** If true, the automation card is shown as "בקרוב" — not editable, not togglable. */
  comingSoon?: boolean;
}

export interface IAutomationSettingField {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'select';
  default?: any;
  options?: Array<{ label: string; value: any }>;
  suffix?: string;
  /** Upper bound for 'number' fields (the input's min is always 1) */
  max?: number;
}
